import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["id", "image", "name", "url", "graphqlEntityMap", "entityProps", "alternativePreHeader", "alternativeHeadline", "category", "modifier", "parsedPropsToDisplay", "propsToDisplay", "propsToDisplayModifiers", "gtmId", "style", "priority", "cardChildren", "gtmChildren", "displayCategory", "displayThumbnail", "displayTitle", "enableOverlay", "enableCarousel", "titleOverlayModifier", "overlayModifier", "gridModifier", "pageBuilderComponents", "__typename"];
import React, { Fragment } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import BlazeLink from '../BlazeLink';
import ClickWrapper from '../ClickWrapper';
import { getModifiers, renderChildren, updateChildrensParent, getClickWrapperOptions } from '../../helpers';
import LazyImage from '../LazyImage';
import { getPublishedListingUrl, useDynamicSizeKey } from './helpers';
import { CARD_STRING, CARD_CHILDREN_WITH_LINKS } from './constants';
import BuildPBComponents from '../../hooks/helpers/buildPBComponents';
const Card = _ref => {
  let {
      id,
      image,
      name,
      url,
      graphqlEntityMap,
      entityProps,
      alternativePreHeader,
      alternativeHeadline,
      category,
      modifier,
      parsedPropsToDisplay,
      propsToDisplay,
      propsToDisplayModifiers,
      gtmId,
      style,
      priority,
      cardChildren,
      gtmChildren,
      displayCategory,
      displayThumbnail,
      displayTitle,
      enableOverlay,
      enableCarousel,
      titleOverlayModifier,
      overlayModifier,
      gridModifier,
      pageBuilderComponents,
      __typename: dataEntity
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const entity = graphqlEntityMap[dataEntity];
  updateChildrensParent(cardChildren, id, entity);
  const parent = {
    itemId: id,
    itemEntity: entity
  };
  const router = useRouter();
  const {
    imageSrc,
    shouldDisplayThumbnail,
    altText
  } = getImageDefinedProps({
    image,
    displayThumbnail,
    name
  });
  const modifiers = getInitialModifiers({
    modifier,
    style,
    otherProps
  });
  const {
    publishedListingPage,
    preHeader,
    label,
    headline
  } = getCategoryDefinedProps({
    alternativePreHeader,
    category,
    entity,
    alternativeHeadline,
    displayCategory,
    name
  });
  const linkTitle = name;
  const dynamicSizeKey = useDynamicSizeKey(CARD_STRING, {
    style,
    gridModifier,
    modifier
  });
  const {
    shouldUseClickWrapper,
    wrapperProps
  } = getClickWrapperOptions(gtmChildren, entityProps, id);
  const Wrapper = shouldUseClickWrapper ? ClickWrapper : Fragment;
  const classNameWithModifiers = getModifiers(CARD_STRING, modifiers);
  const contentWrapperModifier = `${getModifiers('card__content-wrapper', modifiers)} ${enableOverlay && enableCarousel ? overlayModifier : ''}`;
  const titleWrapperModifier = `${getModifiers('card__title', modifiers)} ${enableOverlay && enableCarousel ? titleOverlayModifier : ''}`;
  const handleCardClick = e => {
    if (!url) return;
    const clickEventTag = e.target.tagName.toLowerCase();
    const clickEventRole = e.target.role;
    const shouldTriggerRouteChange = clickEventRole !== 'button' && !CARD_CHILDREN_WITH_LINKS.find(childTag => childTag === clickEventTag);
    if (shouldTriggerRouteChange) router.push(`/Resolver`, url);
  };

  // todo: asses whether we make link wrapper default after wider testing with BLAZE_PB_ADD_CLICK_TO_CARDS
  const useLinkWrapper = url && process.env.BLAZE_PB_ADD_CLICK_TO_CARDS;
  const clickProps = useLinkWrapper ? {
    onClick: handleCardClick,
    href: url,
    ariaLabel: name
  } : {};
  const CardWrapper = useLinkWrapper ? BlazeLink : 'div';
  const childLinkHref = useLinkWrapper ? undefined : url;
  const ChildLinkWrapper = useLinkWrapper ? 'div' : BlazeLink;
  return /*#__PURE__*/React.createElement(Wrapper, wrapperProps, /*#__PURE__*/React.createElement(CardWrapper, _extends({
    className: classNameWithModifiers
  }, clickProps), shouldDisplayThumbnail && /*#__PURE__*/React.createElement("div", {
    className: getModifiers('card__image', modifiers)
  }, /*#__PURE__*/React.createElement(ChildLinkWrapper, {
    gtmId: gtmId,
    href: childLinkHref,
    className: "card__image-link",
    scroll: true
  }, /*#__PURE__*/React.createElement(LazyImage, {
    src: imageSrc,
    alt: altText,
    sizeKey: dynamicSizeKey,
    priority: priority
  }))), /*#__PURE__*/React.createElement("div", {
    className: contentWrapperModifier
  }, /*#__PURE__*/React.createElement("div", {
    className: getModifiers('card__content', modifiers)
  }, displayCategory && (publishedListingPage ? /*#__PURE__*/React.createElement(ChildLinkWrapper, {
    gtmId: gtmId,
    href: publishedListingPage,
    className: "badge badge--label",
    scroll: true
  }, preHeader.name) : /*#__PURE__*/React.createElement("div", {
    className: "badge badge--label"
  }, label)), displayTitle && /*#__PURE__*/React.createElement("div", {
    className: titleWrapperModifier
  }, /*#__PURE__*/React.createElement(ChildLinkWrapper, {
    title: linkTitle,
    gtmId: gtmId,
    href: childLinkHref,
    scroll: true
  }, headline)), !!pageBuilderComponents.length && /*#__PURE__*/React.createElement("div", {
    className: "card__item card__item--full",
    "data-testid": "full-card"
  }, BuildPBComponents(pageBuilderComponents, {
    parent
  })), !!parsedPropsToDisplay.length && /*#__PURE__*/React.createElement("div", {
    className: getModifiers('card__details', modifiers)
  }, parsedPropsToDisplay.map((prop, i) => {
    if (!prop) return null;
    const dynamicKey = `${id}${i}`;
    const propModifier = propsToDisplayModifiers[i] || '';
    const {
      htmlAttribute
    } = propsToDisplay[i] || {};
    const extraProps = {};
    if (htmlAttribute && prop.props.children) {
      extraProps[htmlAttribute] = encodeURIComponent(prop.props.children[2]);
    }
    return /*#__PURE__*/React.createElement("span", _extends({
      key: dynamicKey,
      className: propModifier
    }, extraProps), prop);
  }))), !!cardChildren && /*#__PURE__*/React.createElement("div", {
    className: "card__child-content"
  }, renderChildren(cardChildren)))));
};
Card.propTypes = {
  id: PropTypes.string.isRequired,
  image: PropTypes.shape({
    url: PropTypes.string,
    data: PropTypes.object
  }),
  graphqlEntityMap: PropTypes.object.isRequired,
  __typename: PropTypes.string.isRequired,
  style: PropTypes.string,
  parsedPropsToDisplay: PropTypes.array,
  propsToDisplay: PropTypes.array,
  propsToDisplayModifiers: PropTypes.array,
  url: PropTypes.string,
  name: PropTypes.string.isRequired,
  entityProps: PropTypes.object,
  gtmId: PropTypes.string,
  alternativePreHeader: PropTypes.shape({
    name: PropTypes.string
  }),
  alternativeHeadline: PropTypes.string,
  category: PropTypes.shape({
    name: PropTypes.string,
    publishedListingPage: PropTypes.shape({
      url: PropTypes.string
    })
  }),
  modifier: PropTypes.string,
  displayCategory: PropTypes.bool.isRequired,
  displayThumbnail: PropTypes.bool.isRequired,
  displayTitle: PropTypes.bool.isRequired,
  enableCarousel: PropTypes.bool,
  enableOverlay: PropTypes.bool,
  overlayModifier: PropTypes.string,
  titleOverlayModifier: PropTypes.string,
  cardChildren: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  gtmChildren: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  gridModifier: PropTypes.string,
  priority: PropTypes.bool,
  pageBuilderComponents: PropTypes.array
};
Card.defaultProps = {
  entityProps: {},
  pageBuilderComponents: [],
  image: {
    url: null,
    data: {}
  },
  gtmId: '',
  style: 'portrait',
  parsedPropsToDisplay: [],
  propsToDisplay: [],
  propsToDisplayModifiers: [],
  url: '',
  alternativePreHeader: null,
  alternativeHeadline: '',
  category: null,
  modifier: '',
  cardChildren: [],
  gtmChildren: [],
  gridModifier: '',
  priority: false,
  overlayModifier: '',
  titleOverlayModifier: '',
  enableOverlay: false,
  enableCarousel: false
};
const getCategoryDefinedProps = ({
  alternativePreHeader,
  category,
  entity,
  alternativeHeadline,
  displayCategory,
  name
}) => {
  const hasAlternativePreHeader = alternativePreHeader && alternativePreHeader.name;
  const preHeader = alternativeHeadline && hasAlternativePreHeader ? alternativePreHeader : category;
  const publishedListingPage = getPublishedListingUrl(preHeader);
  const label = preHeader ? preHeader.name : entity;
  const headline = !displayCategory || !hasAlternativePreHeader ? name : alternativeHeadline || name;
  return {
    publishedListingPage,
    preHeader,
    label,
    headline
  };
};
const getImageDefinedProps = ({
  image,
  displayThumbnail,
  name
}) => {
  const {
    url: imageSrc,
    data
  } = image || {};
  const shouldDisplayThumbnail = displayThumbnail && imageSrc;
  const altText = data && data.altText || name;
  return {
    imageSrc,
    shouldDisplayThumbnail,
    altText
  };
};
const getInitialModifiers = ({
  modifier,
  style,
  otherProps
}) => {
  const modifiers = [modifier, style];
  const {
    sponsored,
    featured,
    live
  } = otherProps;
  if (sponsored) modifiers.push('sponsored');
  if (featured) modifiers.push('featured');
  if (live) modifiers.push('live');
  return modifiers;
};
export default Card;
//# sourceMappingURL=Card.js.map